import type { ElementType } from 'react';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import type { InboxConversationAction, InboxSummary } from '@/lib/inbox/types';

/** Gmail's star yellow. */
export const STAR = '#f4b400';

// How each conversation action looks, the same in the swipe, the right-click menu and the thread, on every channel.
// The server lists which actions a conversation offers; the channel decides how read and unread happen on its platform.
export const ACTION_VIEWS: Record<InboxConversationAction, { label: string; short: string; Icon: ElementType; color?: string }> = {
  read: { label: 'Mark as Read', short: 'Read', Icon: MarkEmailReadOutlinedIcon },
  unread: { label: 'Mark as Unread', short: 'Unread', Icon: MarkEmailUnreadOutlinedIcon },
  handled: { label: 'Remove from Focus', short: 'Remove', Icon: TaskAltRoundedIcon, color: 'var(--needs-reply-fg)' },
  unhandled: { label: 'Keep in Focus', short: 'Keep', Icon: PushPinOutlinedIcon },
  star: { label: 'Star', short: 'Star', Icon: StarBorderRoundedIcon },
  unstar: { label: 'Remove star', short: 'Starred', Icon: StarRoundedIcon, color: STAR },
  archive: { label: 'Archive', short: 'Archive', Icon: ArchiveOutlinedIcon },
  trash: { label: 'Delete', short: 'Delete', Icon: DeleteOutlineRoundedIcon },
};

/** Archive and delete take the conversation out of the list, as in Gmail. */
export const LEAVES_LIST: ReadonlySet<InboxConversationAction> = new Set(['archive', 'trash']);

const offered = (row: InboxSummary, action: InboxConversationAction) => (row.actions.includes(action) ? action : null);

/** Swipe right on the phone, and the first action in the menu. */
export const readToggle = (row: InboxSummary) => offered(row, row.unread ? 'read' : 'unread');

/** Swipe left on the phone. A conversation not yet triaged counts as handled. */
export const handledToggle = (row: InboxSummary) => offered(row, row.needs_reply ? 'handled' : 'unhandled');

export const starToggle = (row: InboxSummary) => offered(row, row.starred ? 'unstar' : 'star');

/** The menu on a row and the actions in a thread: read, Focus, then Gmail's own. */
export const rowActions = (row: InboxSummary) =>
  [readToggle(row), handledToggle(row), starToggle(row), offered(row, 'archive'), offered(row, 'trash')].filter((action): action is InboxConversationAction => action !== null);

/** The icon buttons in a mail thread's header, as in Gmail: star, archive, delete. */
export const mailActions = (row: InboxSummary) => [starToggle(row), offered(row, 'archive'), offered(row, 'trash')].filter((action): action is InboxConversationAction => action !== null);
